import { Alert, Box, Button, Checkbox, FormControl, Grid, ListItemText, MenuItem, Paper, Select, Stack, Typography } from "@mui/material"
import { useEffect, useReducer, useState } from "react"
import SaveIcon from '@mui/icons-material/Save'

import useInternalFetch from "../../../../api/apiInternal"
import Loading          from "../../../../components/Loading"
import GeneralError     from "../../../../components/GeneralError"
import config           from "../../../../common/Config"

const GROWTH_OPTIONS = ['Lento', 'Médio', 'Rápido']

const RISK_LEVELS = [
	{ key: 'low', label: 'Baixo' },
	{ key: 'med', label: 'Médio' },
	{ key: 'hig', label: 'Alto'  },
]

const ApprovalPanel = ({ contextId, onSaved }) => {
	const internalFetch             = useInternalFetch()
	const [, forceUpdate]           = useReducer(x => x + 1, 0)
	const [values, setValues]       = useState({ low: [], med: [], hig: [] })
	const [errors, setErrors]       = useState({ low: false, med: false, hig: false })
	const [isLoading, setIsLoading] = useState(true)
	const [isSaving, setIsSaving]   = useState(false)
	const [fatalError, setFatalError] = useState(false)
	const [message, setMessage]     = useState(null)

	useEffect(() => {
		setIsLoading(true)
		setFatalError(false)
		const ctxUrl    = config.apiUrl + '/api/settings/growthRiskParameters' +
			(contextId !== null ? `?contextId=${contextId}` : '')
		const globalUrl = config.apiUrl + '/api/settings/growthRiskParameters'

		Promise.all([
			internalFetch(ctxUrl),
			contextId !== null ? internalFetch(globalUrl) : Promise.resolve(null),
		]).then(([ctxResult, globalResult]) => {
			// Use context params if they exist, otherwise fall back to global
			const result = (ctxResult && !ctxResult.status) ? ctxResult
				: (globalResult && !globalResult.status)    ? globalResult
				: null
			if (result) {
				setValues({
					low: result.lowGrowth ? result.lowGrowth.split(',') : [],
					med: result.medGrowth ? result.medGrowth.split(',') : [],
					hig: result.higGrowth ? result.higGrowth.split(',') : [],
				})
			} else if (ctxResult?.status) {
				setFatalError(true)
			}
			setIsLoading(false)
		})
	}, [contextId])

	const validate = () => {
		const e = {
			low: values.low.length === 0,
			med: values.med.length === 0,
			hig: values.hig.length === 0,
		}
		setErrors(e)
		forceUpdate()
		return !e.low && !e.med && !e.hig
	}

	const handleSave = () => {
		setIsSaving(true)
		if (validate()) {
			internalFetch(config.apiUrl + '/api/settings/growthRiskParameters/', {
				method  : 'POST',
				headers : { 'content-type': 'application/json; charset=utf-8' },
				body    : JSON.stringify({
					contextId : contextId ?? null,
					params    : { lowGrowth: values.low, medGrowth: values.med, higGrowth: values.hig }
				})
			}).then(results => {
				setMessage(results.status
					? { severity: 'error',   text: 'Erro ao salvar parâmetros.' }
					: { severity: 'success', text: 'Parâmetros salvos com sucesso.' }
				)
				if (!results.status) onSaved?.()
				setIsSaving(false)
			})
		} else {
			setMessage({ severity: 'error', text: 'Selecione ao menos um crescimento por nível de risco.' })
			setIsSaving(false)
		}
	}

	if (fatalError) return <GeneralError message="Erro ao carregar parâmetros de aprovação" />

	return (
		<Paper variant="outlined" sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
			<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
				Aprovação
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
				Quais crescimentos são aceitos para cada nível de risco?
			</Typography>

			{isLoading ? <Loading paddingBottom={2} /> : (
				<>
					{message && (
						<Alert severity={message.severity} sx={{ mb: 1.5, py: 0 }} onClose={() => setMessage(null)}>
							{message.text}
						</Alert>
					)}

					<Grid container spacing={1} alignItems="center" mb={1}>
						<Grid item xs={4}>
							<Typography variant="caption" color="text.secondary" fontWeight="bold">Risco</Typography>
						</Grid>
						<Grid item xs={8}>
							<Typography variant="caption" color="text.secondary" fontWeight="bold">Crescimento aceito</Typography>
						</Grid>
					</Grid>

					{RISK_LEVELS.map(({ key, label }) => (
						<Grid container spacing={1} alignItems="center" key={key} mb={0.5}>
							<Grid item xs={4}>
								<Typography variant="body2">{label}</Typography>
							</Grid>
							<Grid item xs={8}>
								<FormControl fullWidth size="small" error={errors[key]}>
									<Select
										multiple
										value={values[key]}
										onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
										renderValue={selected => selected.join(', ')}
									>
										{GROWTH_OPTIONS.map(opt => (
											<MenuItem key={opt} value={opt}>
												<Checkbox size="small" checked={values[key].includes(opt)} />
												<ListItemText primary={opt} />
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
						</Grid>
					))}

					<Box flex={1} />
					<Stack direction="row" justifyContent="flex-end" mt={2}>
						<Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={isSaving}>
							Salvar
						</Button>
					</Stack>
				</>
			)}
		</Paper>
	)
}

export default ApprovalPanel
